import React, { useEffect, useRef, useState } from 'react';

/**
 * ScrollReveal
 * Reusable wrapper that reveals its children when scrolled into view using IntersectionObserver.
 * Props:
 * - children: React nodes
 * - className: additional class names for the wrapper
 * - rootMargin: IntersectionObserver rootMargin (default '0px 0px -10% 0px')
 * - threshold: IntersectionObserver threshold (default 0.15)
 * - once: reveal only once (default true)
 * - delay: optional delay in ms to apply as transition-delay
 */

const ScrollReveal = ({
  children,
  className = '',
  rootMargin = '0px 0px -20% 0px',
  threshold = 0.15,
  once = true,
  delay = 0,
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (once) obs.unobserve(el);
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { root: null, rootMargin, threshold }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [rootMargin, threshold, once]);

  const style = delay ? { transitionDelay: `${delay}ms` } : undefined;
  // Apply global multiplier (from CSS variable) to the provided delay so site-wide speed can be tuned
  let scaledStyle = style;
  if (delay) {
    try {
      const root = typeof window !== 'undefined' && window.getComputedStyle ? window.getComputedStyle(document.documentElement) : null;
      const multRaw = root ? root.getPropertyValue('--scroll-reveal-delay-multiplier') : null;
      const multiplier = multRaw ? parseFloat(multRaw) : 1;
      const scaled = Math.round(delay * (isNaN(multiplier) ? 1 : multiplier));
      scaledStyle = { transitionDelay: `${scaled}ms` };
    } catch (e) {
      // fallback to provided delay
      scaledStyle = style;
    }
  }

  return (
    <div
      ref={ref}
      className={`scroll-reveal ${visible ? 'visible' : ''} ${className}`.trim()}
      style={scaledStyle}
    >
      {children}
    </div>
  );
};

export default ScrollReveal;
